import {
  HomeOrdersTodayQuery,
  OrderChargeStatusEnum,
  OrderStatus,
} from "@dashboard/graphql";
import { RelayToFlat } from "@dashboard/types";
import { IMoney } from "@dashboard/utils/intl";

export type OrderToday = RelayToFlat<
  NonNullable<HomeOrdersTodayQuery["ordersToday"]>
>[number];

export interface SalesToday {
  /**
   * Sum of amounts charged against contributing orders. Zero on a day that
   * simply has no sales yet; null only when the figure genuinely cannot be
   * determined — nothing loaded, or amounts in more than one currency. The
   * two are different facts and must not collapse to one string in the UI.
   */
  collected: IMoney | null;
  /** Orders the figure is made of, most recent first. */
  contributing: OrderToday[];
  /** Eligible orders that have received no payment at all. */
  awaitingPaymentCount: number;
  /**
   * True when the day could not be read in full, so `collected` is not a
   * trustworthy total and the caller must fall back to the ordersTotal
   * aggregate — and say so on the figure itself.
   */
  isPartialDay: boolean;
}

/**
 * The API compares `created` against `created_at__date` in UTC, so the day must
 * be computed in UTC too. A locally-derived date disagrees with the server
 * either side of midnight for a shop running at UTC+1/+2.
 */
export const getUtcToday = (now: Date = new Date()): string =>
  now.toISOString().slice(0, 10);

/**
 * Sums money that shares a currency.
 *
 * `fallbackCurrency` lets an empty list total to zero rather than to "unknown",
 * which matters because a shop with no sales yet today is not the same as a
 * figure we failed to compute. Mixed currencies still return null, because
 * adding them would produce a confidently wrong number.
 */
export const sumMoney = (
  amounts: IMoney[],
  fallbackCurrency?: string,
): IMoney | null => {
  if (amounts.length === 0) {
    return fallbackCurrency ? { amount: 0, currency: fallbackCurrency } : null;
  }

  const { currency } = amounts[0];

  if (amounts.some(money => money.currency !== currency)) {
    return null;
  }

  return {
    amount: amounts.reduce((total, money) => total + money.amount, 0),
    currency,
  };
};

const EXCLUDED_STATUSES = [OrderStatus.CANCELED, OrderStatus.EXPIRED];

/**
 * Derives the sales figure and the orders behind it from one query result.
 *
 * The exclusions happen here rather than in the query because
 * `OrderStatusFilter` cannot express them: it has no EXPIRED, no RETURNED, no
 * PARTIALLY_RETURNED and no negation. Enumerating the "good" statuses instead
 * would drop returned orders, which do count towards the figure — a mistake
 * that stays invisible until a day with a return.
 */
export const deriveSalesToday = (
  data: HomeOrdersTodayQuery | undefined,
  channelCurrency?: string,
): SalesToday => {
  const connection = data?.ordersToday;
  const nodes = (connection?.edges ?? []).map(edge => edge.node);

  const eligible = nodes.filter(
    node => !EXCLUDED_STATUSES.includes(node.status),
  );

  const contributing = eligible.filter(
    node => node.chargeStatus !== OrderChargeStatusEnum.NONE,
  );
  const awaitingPayment = eligible.filter(
    node => node.chargeStatus === OrderChargeStatusEnum.NONE,
  );

  return {
    // Only a loaded response can total to zero; without one the figure is
    // unknown, not nil.
    collected: sumMoney(
      contributing.map(node => node.totalCharged),
      connection ? channelCurrency : undefined,
    ),
    contributing,
    awaitingPaymentCount: awaitingPayment.length,
    isPartialDay: connection?.pageInfo?.hasNextPage ?? false,
  };
};
